import React, { useState, useEffect } from 'react';
import { Snowfall } from 'react-snowfall';

interface ImagePair {
    desktop: string;
    mobile: string;
    alt: string;
    colorway?: string;
    isNew?: boolean;
    tagline?: string;
    description?: string;
    buttonVariant?: string;
    accentColor?: string;
    externalLink?: string;
    desktopWidth?: number;
    desktopHeight?: number;
    mobileWidth?: number;
    mobileHeight?: number;
}

interface HeroCarouselProps {
    images: ImagePair[];
    interval?: number;
    onImageChange?: (currentImage: ImagePair, index: number) => void;
    snowflakeCount?: number;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({
    images,
    interval = 5000,
    onImageChange,
    snowflakeCount = 80
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isInitialRender, setIsInitialRender] = useState(true);

    useEffect(() => {
        const advance = () => {
            setCurrentIndex((prevIndex) => {
                setIsInitialRender(false);
                return (prevIndex + 1) % images.length;
            });
        };

        let timer = setInterval(advance, interval);

        const onVisibilityChange = () => {
            if (document.hidden) {
                clearInterval(timer);
            } else {
                // Restart with a fresh interval so the next slide change is a
                // full interval away — avoids an immediate fire on tab return.
                timer = setInterval(advance, interval);
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [images.length, interval]);

    // Notify parent component when image changes
    useEffect(() => {
        if (onImageChange && images[currentIndex]) {
            onImageChange(images[currentIndex], currentIndex);
        }
    }, [currentIndex, onImageChange, images]);

    // Calculate the previous index for the base layer
    const previousIndex = isInitialRender ? 0 : (currentIndex - 1 + images.length) % images.length;

    // Increase wind and speed when there's more snow
    const isExtraSnow = snowflakeCount > 100;
    const speed: [number, number] = isExtraSnow ? [2, 8] : [0.5, 3];
    const wind: [number, number] = isExtraSnow ? [-3, 5] : [-0.5, 2];

    return (
        <div className="absolute w-full h-[125svh] bottom-0 md:h-full">
            {/* <Snowfall
                snowflakeCount={snowflakeCount}
                color="white"
                speed={speed}
                wind={wind}
                radius={[0.5, 6]}
                style={{ top: '0px', left: 0, width: '100%', height: '100%', zIndex: '1000' }}
            /> */}
            {/* Render all images with opacity control for smoother transitions.
                Plain CSS transitions rather than framer-motion: framer's WAAPI
                animations flash back to their start value for one frame when they
                finish in Chrome. */}
            {images.map((image, index) => (
                <div
                    key={index}
                    style={{
                        opacity: index === currentIndex ? 1 : 0,
                        transition: isInitialRender ? 'none' : 'opacity 1.2s ease-in-out'
                    }}
                    className="absolute inset-0 w-full h-full"
                >
                    <picture className="w-full h-full">
                        <source
                            media="(min-width: 768px)"
                            srcSet={image.desktop}
                        />
                        <source
                            media="(max-width: 767px)"
                            srcSet={image.mobile}
                        />
                        <img
                            src={image.desktop}
                            alt={image.alt}
                            width={image.desktopWidth || 2490}
                            height={image.desktopHeight || 1400}
                            {...({ fetchpriority: index === 0 ? "high" : "low" } as any)}
                            loading={index === 0 ? "eager" : "lazy"}
                            className="absolute inset-0 w-full h-full object-cover hero-image"
                        />
                    </picture>
                </div>
            ))}

            {/* <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent hero-overlay z-[2]" /> */}
        </div>
    );
};

export default HeroCarousel; 